import React, { useEffect } from "react";
import { IconButton, Stack } from "@mui/material";
import { usePollIsOpenState } from "../usePollIsOpenState";
import { PostPollClosedAt } from "../PostPollClosedAt";
import { usePollsState } from "../usePollsState";
import { BasePollType } from "./PostPollFactory";
import { PostPollTextOption } from "../Options/PostPollTextOption";
import { CheckSolidIcon } from "@icons/CheckSolidIcon";
import { CircleIcon } from "@icons/CircleLeft";

type PostPollTextSectionInterface = BasePollType & {
    allowChangeAnswer: boolean;
    showDetailsBeforeUserVote: boolean;
};

export function PostPollTextSection({
    pollClosedAt,
    pollOptionHandler,
    pollOptions: initialPollOptions,
    postId,
    showDetailsBeforeUserVote,
    allowChangeAnswer,
}: PostPollTextSectionInterface) {
    const { open } = usePollIsOpenState(pollClosedAt);
    const {
        totalResponseCount,
        handlePollOptionClick,
        pollOptions,
        hasParticipated,
        userOptionId,
        resetPollOptions,
    } = usePollsState(initialPollOptions, pollOptionHandler, postId);
    useEffect(() => {
        resetPollOptions(initialPollOptions);
    }, [initialPollOptions]);
    const allowVote = open && (allowChangeAnswer || !hasParticipated);

    return (
        <Stack
            gap={"8px"}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {pollOptions.map((pollOption) => {
                const isRespondedByCurrentUser = pollOption.id === userOptionId;
                const votePercentage = totalResponseCount
                    ? Math.round(
                          (pollOption.responsesCount * 100) /
                              totalResponseCount,
                      )
                    : 0;

                return (
                    <PostPollTextOption
                        key={pollOption.id}
                        pollOption={pollOption}
                        handlePollOptionClick={handlePollOptionClick}
                        showDetails={
                            showDetailsBeforeUserVote ||
                            hasParticipated ||
                            !open
                        }
                        enableVoting={allowVote}
                        isRespondedByCurrentUser={isRespondedByCurrentUser}
                        optionPercentage={votePercentage}
                        icon={getOptionIcon(
                            isRespondedByCurrentUser,
                            !allowVote && !isRespondedByCurrentUser,
                        )}
                    />
                );
            })}
            <PostPollClosedAt pollClosedAt={pollClosedAt} />
        </Stack>
    );
}

function getOptionIcon(isRespondedByCurrentUser: boolean, inactive: boolean) {
    if (isRespondedByCurrentUser) {
        return (
            <IconButton
                sx={{
                    padding: 0,
                    backgroundColor: "var(--community-color-primary-text)",
                    width: "24px",
                    height: "24px",
                    "&:hover": {
                        backgroundColor: "var(--community-color-primary-text)",
                    },
                }}
            >
                <CheckSolidIcon
                    color="var(--community-color-snow-white)"
                    width="12px"
                    height="12px"
                />
            </IconButton>
        );
    }

    return inactive ? null : (
        <CircleIcon color={"var(--community-color-transparent-black-50)"} />
    );
}
